/**
 * PrefillBanner — Phase 7-IA Slice 6.
 *
 * Neutral info-blue affordance shown when a section's required-visible
 * fields are already answered (because of screener handoff or
 * save-and-resume hydration), the user hasn't touched any field in
 * this section yet, and the section isn't already marked complete.
 *
 * The banner replaces the empty field list with two actions:
 *
 *   - "Confirm and continue" — invokes `onConfirmAndContinue`, which
 *     the App wires to `markSectionComplete(sectionId)` followed by
 *     `goNext()`.
 *   - "Review answers" — local dismissal. Clears the banner for the
 *     remainder of this component's lifecycle so the user can scroll
 *     through the populated fields. Re-mount (i.e., navigating away
 *     and back) recomputes; if the user still hasn't touched anything,
 *     the banner reappears. That re-show is intentional.
 *
 * Provenance ("from screener" vs "from Firestore") is intentionally
 * not surfaced. The detection heuristic — "looks complete and
 * untouched" — covers both sources without the App needing to tag
 * fields with provenance metadata.
 */
import styles from './SectionRenderer.module.css';

export interface IPrefillBannerProps {
  sectionId: string;
  onConfirmAndContinue: (sectionId: string) => void;
  onReviewAnswers: () => void;
}

export function PrefillBanner(props: IPrefillBannerProps): JSX.Element {
  const { sectionId, onConfirmAndContinue, onReviewAnswers } = props;
  return (
    <div
      // Drop aria-label so the
      // banner's visible message is the source of the accessible name
      // rather than a duplicated short label that suppressed it.
      //
      // Reveal animation on mount so
      // the banner's appearance reads as intentional motion instead
      // of a layout jump. The class is defined globally in tokens.css.
      role="status"
      className={`${styles.prefill_banner} reveal_on_mount`}
      data-prefill-banner-section={sectionId}
    >
      <p className={styles.prefill_banner__message}>
        <span className={styles.prefill_banner__icon} aria-hidden="true">
          ℹ
        </span>
        {/* Wave 2 Slice 7 (2026-05-02) — clearer + warmer. Old copy
         * was passive ("looks complete from your earlier answers");
         * the rewrite invites a quick eyeball-check rather than
         * leaving the applicant unsure if they should re-enter
         * everything. */}
        <span>
          We carried your earlier answers over to this section. Take a
          quick look and confirm, or open it to make changes.
        </span>
      </p>
      <div className={styles.prefill_banner__actions}>
        <button
          type="button"
          className={styles.prefill_banner__primary}
          onClick={() => onConfirmAndContinue(sectionId)}
        >
          Confirm and continue →
        </button>
        <button
          type="button"
          className={styles.prefill_banner__secondary}
          onClick={onReviewAnswers}
        >
          Review answers
        </button>
      </div>
    </div>
  );
}
